import { useNotifications } from '@/contexts/NotificationContext';
import { useUnreadComments } from '@/contexts/UnreadCommentsContext';
import { apiRequest } from '@/lib/queryClient';
import type { QueryClient } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useToast } from './use-toast';
import { useAuth } from './useAuth';

interface WebSocketMessage {
  type: string;
  data: any;
  message?: string;
}

function tryDesktopNotify(title: string, body?: string) {
  try {
    if (!('Notification' in window)) return;
    if (Notification.permission === 'granted') {
      new Notification(title, { body });
    }
  } catch {}
}

async function ensureNotificationPermission() {
  try {
    if (!('Notification' in window)) return;
    if (Notification.permission === 'default') {
      await Notification.requestPermission();
    }
  } catch {}
}

/** Update task in all task-related query caches for instant UI refresh (e.g. reopen) */
function updateTaskInCache(queryClient: QueryClient, task: any) {
  if (!task?.id) return;
  const updateInData = (data: unknown): unknown => {
    if (!data) return data;
    if (Array.isArray(data)) {
      const idx = data.findIndex((t: any) => t?.id === task.id);
      if (idx >= 0) {
        const next = [...data];
        next[idx] = { ...(next[idx] || {}), ...task };
        return next;
      }
      return data;
    }
    if (
      typeof data === 'object' &&
      'items' in data &&
      Array.isArray((data as any).items)
    ) {
      const items = (data as any).items;
      const idx = items.findIndex((t: any) => t?.id === task.id);
      if (idx >= 0) {
        const nextItems = [...items];
        nextItems[idx] = { ...(nextItems[idx] || {}), ...task };
        return { ...data, items: nextItems };
      }
    }
    return data;
  };
  queryClient.setQueriesData(
    {
      predicate: q =>
        Array.isArray(q.queryKey) &&
        (q.queryKey[0] === '/api/tasks' ||
          (q.queryKey[0] === '/api/users' && q.queryKey[2] === 'tasks')),
    },
    (old: unknown) => updateInData(old)
  );
}

export function useWebSocket() {
  const { user, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { addNotification } = useNotifications();
  const { addUnreadCommentTask } = useUnreadComments();
  const wsRef = useRef<WebSocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  const reconnectAttemptsRef = useRef(0);

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      try {
        const message: WebSocketMessage = JSON.parse(event.data);

        // On any WebSocket notification: invalidate and refetch task list so GET /api/tasks is hit for all roles (admin, GM, PM, HOD, staff)
        queryClient.invalidateQueries({ queryKey: ['/api/tasks'] });
        queryClient.invalidateQueries({
          queryKey: ['/api/completed-tasks/pending-acknowledgment'],
        });
        // Refetch ALL task-list queries (active + inactive) - type: 'all' ensures GM/PM/HOD/Staff boards get the API request too, not only admin
        void queryClient.refetchQueries({ queryKey: ['/api/tasks'], type: 'all' });

        switch (message.type) {
          case 'AUTH_SUCCESS':
            setIsConnected(true);
            reconnectAttemptsRef.current = 0;
            console.log('WebSocket authenticated successfully');
            break;

          case 'TASK_CREATED':
            // Add notification for task creation
            addNotification({
              type: 'task_created',
              title: 'New Task Created',
              message: `"${message.data.title}" has been created`,
              taskId: message.data.id,
            });
            // Invalidate and refresh task-related queries
            queryClient.invalidateQueries({ queryKey: ['/api/tasks'] });
            queryClient.invalidateQueries({
              queryKey: ['/api/analytics/task-stats'],
            });
            // Invalidate and refetch user-specific task queries so "Created by" shows in real time
            const userTasksPredicate = (query: { queryKey: unknown[] }) =>
              Array.isArray(query.queryKey) &&
              query.queryKey[0] === '/api/users' &&
              query.queryKey[2] === 'tasks';
            const userTasksSingleKey = (query: { queryKey: unknown[] }) =>
              Array.isArray(query.queryKey) &&
              typeof query.queryKey[0] === 'string' &&
              query.queryKey[0].startsWith('/api/users/') &&
              query.queryKey[0].endsWith('/tasks');
            queryClient.invalidateQueries({ predicate: userTasksPredicate });
            queryClient.refetchQueries({ predicate: userTasksPredicate });
            queryClient.invalidateQueries({ predicate: userTasksSingleKey });
            break;

          case 'TASK_UPDATED':
            // Update task in cache first for instant UI refresh (e.g. reopened task)
            updateTaskInCache(queryClient, message.data);
            addNotification({
              type: 'task_updated',
              title: 'Task Updated',
              message: `"${message.data.title}" has been updated`,
              taskId: message.data.id,
            });
            // Comprehensive invalidation for all task-related queries
            queryClient.invalidateQueries({ queryKey: ['/api/tasks'] });
            queryClient.invalidateQueries({
              queryKey: ['/api/analytics/task-stats'],
            });
            queryClient.invalidateQueries({
              predicate: query =>
                Array.isArray(query.queryKey) &&
                query.queryKey[0] === '/api/tasks',
            });
            queryClient.invalidateQueries({
              predicate: query =>
                Array.isArray(query.queryKey) &&
                query.queryKey[0] === '/api/users' &&
                query.queryKey[2] === 'tasks',
            });
            queryClient.invalidateQueries({
              predicate: query =>
                Array.isArray(query.queryKey) &&
                query.queryKey.some(
                  key => typeof key === 'string' && key.includes('tasks')
                ),
            });
            // Force immediate refetch so assignee sees updates (e.g. reopened tasks) without refresh
            queryClient.refetchQueries({ queryKey: ['/api/tasks'] });
            queryClient.refetchQueries({
              predicate: query =>
                Array.isArray(query.queryKey) &&
                query.queryKey[0] === '/api/users' &&
                query.queryKey[2] === 'tasks',
            });
            break;

          case 'TASK_ASSIGNED':
            // Add notification for task assignment
            addNotification({
              type: 'task_assigned',
              title: 'Task Assigned',
              message: `"${message.data.title}" has been assigned`,
              taskId: message.data.id,
            });
            // Comprehensive invalidation for all task-related queries
            queryClient.invalidateQueries({ queryKey: ['/api/tasks'] });
            queryClient.invalidateQueries({
              queryKey: ['/api/analytics/task-stats'],
            });
            // Invalidate all queries that start with '/api/tasks'
            queryClient.invalidateQueries({
              predicate: query =>
                Array.isArray(query.queryKey) &&
                query.queryKey[0] === '/api/tasks',
            });
            // Invalidate user-specific task queries
            queryClient.invalidateQueries({
              predicate: query =>
                Array.isArray(query.queryKey) &&
                query.queryKey[0] === '/api/users' &&
                query.queryKey[2] === 'tasks',
            });
            // Invalidate all queries that contain 'tasks' in the key
            queryClient.invalidateQueries({
              predicate: query =>
                Array.isArray(query.queryKey) &&
                query.queryKey.some(
                  key => typeof key === 'string' && key.includes('tasks')
                ),
            });
            console.log(
              'WebSocket: TASK_ASSIGNED - All task queries invalidated'
            );
            break;

          case 'TASK_PENDING_APPROVAL':
            // Add notification for pending approval
            addNotification({
              type: 'task_pending_approval',
              title: 'Task Pending Approval',
              message: `"${message.data.title}" is waiting for your approval`,
              taskId: message.data.id,
            });
            toast({
              title: 'Task Pending Approval',
              description: `A task "${message.data.title}" is waiting for your approval`,
              duration: 5000,
            });
            tryDesktopNotify(
              'Task Pending Approval',
              `${message.data.title} is waiting for your approval`
            );
            // Invalidate and refresh task-related queries
            queryClient.invalidateQueries({ queryKey: ['/api/tasks'] });
            queryClient.invalidateQueries({
              queryKey: ['/api/analytics/task-stats'],
            });
            // Also invalidate user-specific task queries
            queryClient.invalidateQueries({
              predicate: query =>
                Array.isArray(query.queryKey) &&
                query.queryKey[0] === '/api/users' &&
                query.queryKey[2] === 'tasks',
            });
            break;

          case 'HELP_REQUESTED':
            // Add notification for help request
            addNotification({
              type: 'help_requested',
              title: 'Help Request',
              message: `Someone needs your help with a task`,
              taskId: message.data.taskId,
            });
            toast({
              title: 'Help Request Received',
              description: `Someone needs your help with a task`,
              duration: 5000,
            });
            tryDesktopNotify(
              'Help Request',
              'Someone needs your help with a task'
            );
            // Invalidate and refresh task-related queries to show help tasks
            queryClient.invalidateQueries({ queryKey: ['/api/tasks'] });
            queryClient.invalidateQueries({ queryKey: ['/api/users'] });
            queryClient.invalidateQueries({
              queryKey: ['/api/analytics/task-stats'],
            });
            break;

          case 'TASK_HELP_DONE':
            // Add notification for help done
            addNotification({
              type: 'task_help_done',
              title: 'Help Provided',
              message: `Help has been provided for: "${message.data.title}"`,
              taskId: message.data.id,
            });
            toast({
              title: 'Help Provided',
              description: `Help has been provided for: ${message.data.title}`,
              duration: 5000,
            });
            tryDesktopNotify(
              'Help Provided',
              `Help has been provided for: ${message.data.title}`
            );
            // Invalidate and refresh task-related queries
            queryClient.invalidateQueries({ queryKey: ['/api/tasks'] });
            queryClient.invalidateQueries({
              queryKey: ['/api/analytics/task-stats'],
            });
            // Also invalidate user-specific task queries
            queryClient.invalidateQueries({
              predicate: query =>
                Array.isArray(query.queryKey) &&
                query.queryKey[0] === '/api/users' &&
                query.queryKey[2] === 'tasks',
            });
            break;

          case 'TASK_ASSIGNED_TO_YOU':
            // Add notification for task assignment to you
            addNotification({
              type: 'task_assigned',
              title: 'Task Assigned to You',
              message: `You've been assigned: "${message.data.title}"`,
              taskId: message.data.id,
            });
            toast({
              title: 'New Task Assigned',
              description: `You've been assigned: ${message.data.title}`,
              duration: 5000,
            });
            tryDesktopNotify(
              'New Task Assigned',
              `You were assigned: ${message.data.title}`
            );
            queryClient.invalidateQueries({ queryKey: ['/api/tasks'] });
            queryClient.invalidateQueries({
              queryKey: ['/api/tasks/my-tasks'],
            });
            // Invalidate and refetch user-specific task list so "My Board" and "Created by" update in real time
            const userTasksPredicateAssign = (query: { queryKey: unknown[] }) =>
              Array.isArray(query.queryKey) &&
              query.queryKey[0] === '/api/users' &&
              query.queryKey[2] === 'tasks';
            const userTasksSingleKeyAssign = (query: { queryKey: unknown[] }) =>
              Array.isArray(query.queryKey) &&
              typeof query.queryKey[0] === 'string' &&
              query.queryKey[0].startsWith('/api/users/') &&
              query.queryKey[0].endsWith('/tasks');
            queryClient.invalidateQueries({
              predicate: userTasksPredicateAssign,
            });
            queryClient.refetchQueries({ predicate: userTasksPredicateAssign });
            queryClient.invalidateQueries({
              predicate: userTasksSingleKeyAssign,
            });
            queryClient.invalidateQueries({
              queryKey: ['/api/analytics/task-stats'],
            });
            break;

          case 'TASK_APPROVED':
            // Add notification for task approval
            addNotification({
              type: 'task_approved',
              title: 'Task Approved',
              message: `"${message.data.title}" has been approved`,
              taskId: message.data.id,
            });
            queryClient.invalidateQueries({ queryKey: ['/api/tasks'] });
            queryClient.invalidateQueries({
              queryKey: ['/api/analytics/task-stats'],
            });
            // Also invalidate user-specific task queries
            queryClient.invalidateQueries({
              predicate: query =>
                Array.isArray(query.queryKey) &&
                query.queryKey[0] === '/api/users' &&
                query.queryKey[2] === 'tasks',
            });
            break;

          case 'TASK_APPROVED_FOR_YOU':
            // Add notification for your task being approved
            addNotification({
              type: 'task_approved',
              title: 'Your Task Approved',
              message: `Your task "${message.data.title}" has been approved!`,
              taskId: message.data.id,
            });
            toast({
              title: 'Task Approved',
              description: `Your task "${message.data.title}" has been approved!`,
              duration: 5000,
            });
            tryDesktopNotify(
              'Task Approved',
              `${message.data.title} was approved`
            );
            queryClient.invalidateQueries({ queryKey: ['/api/tasks'] });
            break;

          case 'TASK_COMPLETED_BY_ASSIGNEE':
            // Task creator only: assignee completed the task (like admin sees in portal)
            addNotification({
              type: 'task_completed_by_assignee',
              title: 'Task Completed',
              message: `"${message.data.title}" has been completed by ${message.data.completedByName || 'assignee'}`,
              taskId: message.data.id,
            });
            toast({
              title: 'Task Completed',
              description: `"${message.data.title}" was completed by ${message.data.completedByName || 'assignee'}`,
              duration: 5000,
            });
            tryDesktopNotify(
              'Task Completed',
              `${message.data.title} completed by ${message.data.completedByName || 'assignee'}`
            );
            queryClient.invalidateQueries({ queryKey: ['/api/tasks'] });
            queryClient.invalidateQueries({
              queryKey: ['/api/completed-tasks/pending-acknowledgment'],
            });
            queryClient.invalidateQueries({
              predicate: query =>
                Array.isArray(query.queryKey) &&
                query.queryKey[0] === '/api/users' &&
                query.queryKey[2] === 'tasks',
            });
            break;

          case 'TASK_DISAPPROVED':
            // Add notification for task disapproval
            addNotification({
              type: 'task_disapproved',
              title: 'Task Disapproved',
              message: `"${message.data.title}" has been disapproved`,
              taskId: message.data.id,
            });
            queryClient.invalidateQueries({ queryKey: ['/api/tasks'] });
            queryClient.invalidateQueries({
              queryKey: ['/api/analytics/task-stats'],
            });
            // Also invalidate user-specific task queries
            queryClient.invalidateQueries({
              predicate: query =>
                Array.isArray(query.queryKey) &&
                query.queryKey[0] === '/api/users' &&
                query.queryKey[2] === 'tasks',
            });
            break;

          case 'TASK_DISAPPROVED_FOR_YOU':
            // Add notification for your task being disapproved
            addNotification({
              type: 'task_disapproved',
              title: 'Your Task Disapproved',
              message: `Your task "${message.data.title}" has been disapproved and needs rework`,
              taskId: message.data.id,
            });
            toast({
              title: 'Task Disapproved',
              description: `Your task "${message.data.title}" has been disapproved and needs rework`,
              duration: 5000,
            });
            tryDesktopNotify(
              'Task Disapproved',
              `${message.data.title} was disapproved and needs rework`
            );
            queryClient.invalidateQueries({ queryKey: ['/api/tasks'] });
            break;

          case 'TASK_DROPPED':
            // Add notification for task dropping
            addNotification({
              type: 'task_dropped',
              title: 'Task Dropped',
              message: `"${message.data.title}" has been dropped/cancelled`,
              taskId: message.data.id,
            });
            toast({
              title: 'Task Dropped',
              description: `"${message.data.title}" has been dropped/cancelled`,
              duration: 5000,
            });
            // Invalidate and refetch to get latest data
            queryClient.invalidateQueries({ queryKey: ['/api/tasks'] });
            queryClient.invalidateQueries({
              queryKey: ['/api/analytics/task-stats'],
            });
            // Also invalidate user-specific task queries
            queryClient.invalidateQueries({
              predicate: query =>
                Array.isArray(query.queryKey) &&
                (query.queryKey[0] === '/api/tasks' ||
                  (query.queryKey[0] === '/api/users' &&
                    query.queryKey[2] === 'tasks')),
            });
            // Explicitly refetch ALL matching queries (including inactive ones)
            queryClient.refetchQueries({
              queryKey: ['/api/tasks'],
              type: 'all', // Refetch all queries, not just active ones
            });
            break;

          case 'TASK_DROPPED_FOR_YOU':
            // Add notification for your task being dropped
            addNotification({
              type: 'task_dropped',
              title: 'Your Task Dropped',
              message: `Your task "${message.data.title}" has been dropped/cancelled`,
              taskId: message.data.id,
            });
            toast({
              title: 'Task Dropped',
              description: `Your task "${message.data.title}" has been dropped/cancelled`,
              duration: 5000,
            });
            tryDesktopNotify(
              'Task Dropped',
              `${message.data.title} was dropped/cancelled`
            );
            // Invalidate and refetch to get latest data
            queryClient.invalidateQueries({ queryKey: ['/api/tasks'] });
            queryClient.invalidateQueries({
              predicate: query =>
                Array.isArray(query.queryKey) &&
                (query.queryKey[0] === '/api/tasks' ||
                  (query.queryKey[0] === '/api/users' &&
                    query.queryKey[2] === 'tasks')),
            });
            // Explicitly refetch ALL matching queries (including inactive ones)
            queryClient.refetchQueries({
              queryKey: ['/api/tasks'],
              type: 'all', // Refetch all queries, not just active ones
            });
            break;

          case 'TASK_PRIORITY_UPDATED':
            // Handle task priority updates - refresh board without notification
            console.log(
              'WebSocket: TASK_PRIORITY_UPDATED received',
              message.data
            );

            // Invalidate all task-related queries comprehensively
            queryClient.invalidateQueries({ queryKey: ['/api/tasks'] });
            queryClient.invalidateQueries({
              queryKey: ['/api/analytics/task-stats'],
            });

            // Invalidate user-specific task queries (for manager personal view)
            queryClient.invalidateQueries({
              predicate: query =>
                Array.isArray(query.queryKey) &&
                query.queryKey[0] === '/api/users' &&
                query.queryKey[2] === 'tasks',
            });

            // Invalidate all queries that start with '/api/tasks' (includes team-scoped queries)
            queryClient.invalidateQueries({
              predicate: query =>
                Array.isArray(query.queryKey) &&
                query.queryKey[0] === '/api/tasks',
            });

            console.log(
              'WebSocket: All task queries invalidated for TASK_PRIORITY_UPDATED'
            );
            break;

          case 'TASK_PRIORITIES_UPDATED':
            // Handle bulk task priority updates - refresh board without notification
            queryClient.invalidateQueries({ queryKey: ['/api/tasks'] });
            queryClient.invalidateQueries({
              queryKey: ['/api/analytics/task-stats'],
            });

            // Invalidate user-specific task queries (for manager personal view)
            queryClient.invalidateQueries({
              predicate: query =>
                Array.isArray(query.queryKey) &&
                query.queryKey[0] === '/api/users' &&
                query.queryKey[2] === 'tasks',
            });

            // Invalidate all queries that start with '/api/tasks' (includes team-scoped queries)
            queryClient.invalidateQueries({
              predicate: query =>
                Array.isArray(query.queryKey) &&
                query.queryKey[0] === '/api/tasks',
            });
            break;

          case 'TIME_ENTRY_CREATED':
          case 'TIME_ENTRY_UPDATED':
            queryClient.invalidateQueries({ queryKey: ['/api/tasks'] });
            queryClient.invalidateQueries({
              queryKey: ['/api/analytics/task-stats'],
            });
            break;

          case 'TIMER_STARTED':
            toast({
              title: 'Timer Started',
              description: `Timer started for: ${message.data.title}`,
              duration: 3000,
            });
            tryDesktopNotify('Timer Started', `For: ${message.data.title}`);
            queryClient.invalidateQueries({ queryKey: ['/api/tasks'] });
            break;

          case 'TIMER_STOPPED':
            const { task, timeEntry } = message.data;
            const duration = Math.round((timeEntry.duration / 60) * 100) / 100; // Convert to hours
            toast({
              title: 'Timer Stopped',
              description: `Logged ${duration}h for: ${task.title}`,
              duration: 3000,
            });
            tryDesktopNotify(
              'Timer Stopped',
              `Logged ${duration}h for: ${task.title}`
            );
            queryClient.invalidateQueries({ queryKey: ['/api/tasks'] });
            break;

          case 'EXTENSION_REQUESTED':
            // Task creator (GM/PM/HOD) receives when assignee requests due date extension
            addNotification({
              type: 'extension_request',
              title: 'Due Date Extension Requested',
              message: `Assignee requested ${message.data.requestedDays || ''} day(s) extension for "${message.data.taskTitle || 'task'}"`,
              taskId: message.data.taskId,
            });
            toast({
              title: 'Extension Request',
              description: `Assignee requested ${message.data.requestedDays || ''} day(s) extension for "${message.data.taskTitle || 'task'}". Open the task to extend the due date.`,
              duration: 6000,
            });
            if (message.data.taskId) {
              queryClient.invalidateQueries({
                queryKey: [`/api/tasks/${message.data.taskId}/comments`],
              });
            }
            break;

          case 'COMMENT_ADDED':
            addNotification({
              type: 'comment_added',
              title: 'New Comment',
              message: `A comment was added to "${message.data.taskTitle || 'a task'}"`,
              taskId: message.data.taskId,
            });
            if (message.data.taskId) {
              addUnreadCommentTask(message.data.taskId);
              queryClient.invalidateQueries({
                queryKey: [`/api/tasks/${message.data.taskId}/comments`],
              });
            }
            break;

          case 'HELP_REQUEST_CREATED':
            // Add notification for help request creation
            addNotification({
              type: 'help_request_created',
              title: 'New Help Request',
              message: `Help request: "${message.data.subject}"`,
              taskId: message.data.taskId,
            });
            toast({
              title: 'New Help Request',
              description: `Help request: ${message.data.subject}`,
              duration: 5000,
            });
            tryDesktopNotify(
              'New Help Request',
              `Help request: ${message.data.subject}`
            );
            queryClient.invalidateQueries({ queryKey: ['/api/help-requests'] });
            break;

          case 'HELP_REQUEST_UPDATED':
            // Add notification for help request update
            addNotification({
              type: 'help_request_updated',
              title: 'Help Request Updated',
              message: `Help request "${message.data.subject}" has been updated`,
              taskId: message.data.taskId,
            });
            queryClient.invalidateQueries({ queryKey: ['/api/help-requests'] });
            break;

          case 'TASK_REOPENED':
            // Direct event to assignee(s) - update cache, force API refetch so board updates in real time, toast
            const assigneeId = message.data?.assignedTo;
            updateTaskInCache(queryClient, message.data);
            queryClient.invalidateQueries({ queryKey: ['/api/tasks'] });
            queryClient.invalidateQueries({
              predicate: query =>
                Array.isArray(query.queryKey) &&
                query.queryKey[0] === '/api/users' &&
                query.queryKey[2] === 'tasks',
            });
            queryClient.invalidateQueries({
              queryKey: ['/api/analytics/task-stats'],
            });
            // Force immediate refetch of all task list queries so GET /api/tasks (and user tasks) is called and board updates
            void queryClient.refetchQueries({
              predicate: query =>
                Array.isArray(query.queryKey) &&
                (query.queryKey[0] === '/api/tasks' ||
                  (query.queryKey[0] === '/api/users' &&
                    query.queryKey[2] === 'tasks')),
            });
            if (assigneeId) {
              void queryClient.fetchQuery({
                queryKey: ['/api/users', assigneeId, 'tasks'],
                queryFn: async () => {
                  const res = await apiRequest(
                    'GET',
                    `/api/users/${assigneeId}/tasks`
                  );
                  return res.json();
                },
              });
            }
            // Dispatch custom event so Board can also refetch if it uses a different query key
            if (typeof window !== 'undefined') {
              window.dispatchEvent(
                new CustomEvent('task-reopened', {
                  detail: { assigneeId, task: message.data },
                })
              );
            }
            toast({
              title: 'Task Reopened',
              description: `"${message.data?.title || 'A task'}" has been reopened and is back in your list.`,
            });
            break;

          case 'TASK_STATUS_CHANGED':
            // Update task in cache first for instant UI refresh (e.g. reopened task)
            updateTaskInCache(queryClient, message.data);
            addNotification({
              type: 'task_status_changed',
              title: 'Task Status Changed',
              message: `"${message.data.title}" status changed from ${message.data.oldStatus} to ${message.data.newStatus}`,
              taskId: message.data.id,
            });
            // Same comprehensive invalidation as TASK_UPDATED so assignee board refreshes (e.g. on reopen)
            queryClient.invalidateQueries({ queryKey: ['/api/tasks'] });
            queryClient.invalidateQueries({
              queryKey: ['/api/analytics/task-stats'],
            });
            queryClient.invalidateQueries({
              predicate: query =>
                Array.isArray(query.queryKey) &&
                query.queryKey[0] === '/api/tasks',
            });
            queryClient.invalidateQueries({
              predicate: query =>
                Array.isArray(query.queryKey) &&
                query.queryKey[0] === '/api/users' &&
                query.queryKey[2] === 'tasks',
            });
            queryClient.invalidateQueries({
              predicate: query =>
                Array.isArray(query.queryKey) &&
                query.queryKey.some(
                  key => typeof key === 'string' && key.includes('tasks')
                ),
            });
            queryClient.refetchQueries({ queryKey: ['/api/tasks'] });
            queryClient.refetchQueries({
              predicate: query =>
                Array.isArray(query.queryKey) &&
                query.queryKey[0] === '/api/users' &&
                query.queryKey[2] === 'tasks',
            });
            break;

          case 'COMMENT_UPDATED':
          case 'COMMENT_DELETED':
            // Invalidate all task comments queries to ensure real-time updates
            queryClient.invalidateQueries({
              predicate: query =>
                query.queryKey[0] === '/api/tasks' &&
                query.queryKey[2] === 'comments',
            });
            break;

          default:
            console.log('Unknown WebSocket message type:', message.type);
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    },
    [queryClient, toast, addNotification, addUnreadCommentTask]
  );

  const connect = useCallback(() => {
    if (
      !isAuthenticated ||
      !user?.id ||
      wsRef.current?.readyState === WebSocket.OPEN
    ) {
      return;
    }

    try {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${window.location.host}/ws`;

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = async () => {
        console.log('WebSocket connected');
        // Request desktop notification permission (one-time)
        await ensureNotificationPermission();
        // Authenticate with the server
        ws.send(
          JSON.stringify({
            type: 'AUTH',
            userId: user.id,
          })
        );
      };

      ws.onmessage = handleMessage;

      ws.onclose = () => {
        console.log('WebSocket disconnected');
        setIsConnected(false);

        // Attempt to reconnect with exponential backoff
        if (reconnectAttemptsRef.current < 5) {
          const delay = Math.pow(2, reconnectAttemptsRef.current) * 1000; // 1s, 2s, 4s, 8s, 16s
          reconnectAttemptsRef.current++;

          reconnectTimeoutRef.current = setTimeout(() => {
            console.log(
              `Attempting to reconnect... (attempt ${reconnectAttemptsRef.current})`
            );
            connect();
          }, delay);
        }
      };

      ws.onerror = error => {
        console.error('WebSocket error:', error);
        setIsConnected(false);
      };
    } catch (error) {
      console.error('Failed to create WebSocket connection:', error);
    }
  }, [isAuthenticated, user?.id, handleMessage]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setIsConnected(false);
    reconnectAttemptsRef.current = 0;
  }, []);

  useEffect(() => {
    if (isAuthenticated && user?.id) {
      connect();
    } else {
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [isAuthenticated, user?.id, connect, disconnect]);

  return {
    isConnected,
    connect,
    disconnect,
  };
}
